import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Save } from "lucide-react";
import { SCHEMAS } from "./schemas";
import { FieldRow, buildPayload } from "./Field";
import { SubRecordList } from "./SubRecordList";

const variantFields = SCHEMAS.variants.fields.filter((f) => f.key !== "product_slug");
const tierFields = SCHEMAS.bulk_tiers.fields;

export const ProductEditor = ({ slug, onBack }) => {
  const [form, setForm] = useState({ status: "active" });
  const [savedSlug, setSavedSlug] = useState(slug || null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("general");

  useEffect(() => {
    if (!slug) return;
    api.get(`/admin/data/products`).then(({ data }) => {
      const p = data.find((x) => x.slug === slug);
      if (p) setForm(p);
    }).catch(() => {});
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveGeneral = async () => {
    if (!form.name || !form.slug) { toast.error("Name and slug are required"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/crud/products`, buildPayload(SCHEMAS.products.fields, form));
      setSavedSlug(form.slug);
      toast.success("Product saved — you can now add variants and pricing tiers");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="product-editor">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-white/10 text-slate-300" data-testid="product-back"><ArrowLeft size={18} /></button>
        <h1 className="font-serif text-3xl text-white">{savedSlug ? (form.name || "Product") : "New Product"}</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-black/40 border border-white/10">
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="variants" data-testid="tab-variants" disabled={!savedSlug}>Variants & Inventory</TabsTrigger>
          <TabsTrigger value="tiers" data-testid="tab-tiers" disabled={!savedSlug}>Pricing Tiers</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-6">
          <div className="glass rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
            {SCHEMAS.products.fields.filter((f) => !f.caseOnly || form.product_type === "case").map((f) => (
              f.key === "slug" && savedSlug ? (
                <div key="slug">
                  <label className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-1.5 block">Slug (locked)</label>
                  <div className="h-10 rounded-md bg-black/20 border border-white/10 text-slate-400 px-3 text-sm flex items-center font-mono" data-testid="field-slug-locked">{form.slug}</div>
                </div>
              ) : <FieldRow key={f.key} f={f} value={form[f.key]} onChange={(v) => set(f.key, v)} />
            ))}
          </div>
          <Button onClick={saveGeneral} disabled={saving} className="btn-primary rounded-lg mt-4" data-testid="product-save-general"><Save size={15} className="mr-2" />{saving ? "Saving…" : "Save Product"}</Button>
        </TabsContent>

        <TabsContent value="variants" className="mt-6">
          <p className="text-xs text-slate-400 mb-4">Add each strength/SKU with its own price, inventory and wholesale price.</p>
          {savedSlug && <SubRecordList collection="variants" fields={variantFields} fixed={{ product_slug: savedSlug }} singular="Variant" />}
        </TabsContent>

        <TabsContent value="tiers" className="mt-6">
          <p className="text-xs text-slate-400 mb-4">Volume-discount tiers for <span className="text-cyan-300">{savedSlug}</span>. If none are set, the store's global tiers apply.</p>
          {savedSlug && <SubRecordList collection="bulk_tiers" fields={tierFields} fixed={{ product_slug: savedSlug }} singular="Tier" />}
        </TabsContent>
      </Tabs>
    </div>
  );
};
